import { useCallback, useRef, useState } from 'react';
import { sendPaymentRecoveryEvent } from '../utils/paymentRecoveryAnalytics';

// One owner per payment entry: a late wallet cancel cannot release another entry.
export function useTrialPaymentAttempt(controller, telemetry = {}) {
  const owner = useRef({});
  const recovery = useRef(null);
  const telemetryRef = useRef(telemetry);
  telemetryRef.current = telemetry;
  const record = useCallback((name, status = 'unknown') => {
    if (!recovery.current) return;
    try {
      const { startedAt, paymentPath, ...context } = recovery.current;
      sendPaymentRecoveryEvent(name, { paymentPath, payload: {
        ...telemetryRef.current, ...context, status,
        elapsed_ms: Math.max(0, Date.now() - startedAt),
      } });
    } catch (_) { /* Observability must never affect a payment. */ }
  }, []);
  const phase = useRef('idle');
  const [pending, setPending] = useState(null);
  const [checking, setChecking] = useState(false);
  const checkInFlight = useRef(false);
  const begin = controller?.begin, finish = controller?.finish, refresh = controller?.refresh;
  const acquire = useCallback(() => {
    if (phase.current !== 'idle') return false;
    if (begin && !begin(owner.current)) return false;
    phase.current = 'sheet'; return true;
  }, [begin]);
  const release = useCallback(() => {
    if (!finish) { phase.current = 'idle'; return; }
    if (['succeeded', 'pending'].includes(phase.current)) return;
    phase.current = 'idle'; finish?.(owner.current);
  }, [finish]);
  const click = useCallback(event => {
    if (!acquire()) { event.preventDefault(); return false; }
    return true;
  }, [acquire]);
  const cancel = useCallback(() => { if (phase.current === 'sheet') release(); }, [release]);
  const run = useCallback(async (work, wallet = false) => {
    if (!(wallet && phase.current === 'sheet') && !acquire()) return false;
    phase.current = 'started';
    try { await work(); } finally { release(); }
    return true;
  }, [acquire, release]);
  const success = useCallback(() => {
    phase.current = 'succeeded'; setPending(null);
    record('trial_payment_recovered', 'succeeded'); recovery.current = null;
  }, [record]);
  const hold = useCallback((secret, onSuccess, context = {}) => {
    phase.current = 'pending'; setPending({ secret, onSuccess });
    if (!recovery.current) {
      recovery.current = {
        recovery_id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
        startedAt: Date.now(), paymentPath: context.paymentPath,
        reason: context.reason || 'status_unknown', can_check_status: Boolean(secret),
        transaction_id: typeof secret === 'string' ? (secret.match(/^(pi_[a-zA-Z0-9]+)_secret_/)?.[1] || null) : null,
      };
      record('trial_payment_blocked');
    }
  }, [record]);
  const changed = useCallback(error => {
    if (error?.response?.status !== 409 || error?.response?.data?.code !== 'PAYMENT_OFFER_CHANGED') return false;
    phase.current = 'idle'; refresh?.(); return true;
  }, [refresh]);
  const check = async stripe => {
    if (checkInFlight.current || phase.current !== 'pending' || !pending?.secret) return;
    checkInFlight.current = true; setChecking(true);
    record('trial_payment_status_check');
    try {
      const { paymentIntent } = await stripe.retrievePaymentIntent(pending.secret);
      record('trial_payment_status_result', paymentIntent?.status || 'lookup_failed');
      if (paymentIntent?.status === 'succeeded') {
        success(); await pending.onSuccess(paymentIntent);
      } else if (['requires_payment_method', 'canceled'].includes(paymentIntent?.status)) {
        phase.current = 'idle'; setPending(null); finish?.(owner.current);
        record('trial_payment_recovered', paymentIntent.status); recovery.current = null;
      }
    } catch (_) {
      record('trial_payment_status_result', 'lookup_failed');
      // An unknown result never authorizes another payment.
    }
    finally { checkInFlight.current = false; setChecking(false); }
  };
  return { click, cancel, run, success, hold, changed, pending, checking, check,
    succeeded: () => phase.current === 'succeeded' };
}

export const isDefiniteTrialFailure = data => data?.code === 'PAYMENT_DECLINED' ||
  ['requires_payment_method', 'canceled'].includes(data?.status);
